/**
 * TODO: NEXUS V3.29.0
 * This Service was partially implemented.
 * GET Periods API integration is pending to implementation.
 */
import { getUserSession, isEqualObject } from 'ui-smartforce-settings';
import {
  AnalyticsConfig,
  AnalyticsFiltersRequest,
  AnalyticsPeriodOptions,
  Widget,
  WidgetBase
} from '../../../Models';
import { WIDGET_TYPES } from '../../../Constants/Analytics';
import { apiGet, apiPost } from '../../../Helpers';
import { getWidgetWithData } from '../Common/Common';
import { APP_NEXUS_API_BASE_URL } from '../../../Constants/Url';

const LOCAL_STORAGE_KEY = 'Smartforce.Nexus.WidgetsFavourites.WS_ID.';

export class AnalyticsServiceClass {
  private config: AnalyticsConfig | undefined;
  private widgetListLite: WidgetBase[] = [];
  private filters: AnalyticsFiltersRequest | undefined;

  getConfig() {
    return this.config;
  }

  getWidgetListLite() {
    return this.widgetListLite;
  }

  async updateConfig(
    workspaceId: string,
    filters?: AnalyticsFiltersRequest
  ): Promise<AnalyticsConfig> {
    if (this.config && isEqualObject(filters, this.filters)) {
      return Promise.resolve(this.config);
    } else {
      const url = `${APP_NEXUS_API_BASE_URL}/analytics/generate`;
      const period = filters?.period || 'month';

      const fetchResp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `bearer ${getUserSession().access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(
          filters
            ? {
                workspace_id: workspaceId,
                period: filters.period,
                period_value: filters.period_value,
                filters:
                  Object.keys(filters.filters).length > 0
                    ? filters.filters
                    : undefined
              }
            : { workspace_id: workspaceId, period: period }
        )
      });

      if (fetchResp.ok) {
        return fetchResp.json().then((config: AnalyticsConfig) => {
          let widgets: Widget[] = [];
          this.widgetListLite = [];

          config.widgets
            .filter((w) => WIDGET_TYPES.includes(w.component.name))
            .forEach((w: Widget, index: number) => {
              widgets = [...widgets, getWidgetWithData(config, w.id, index)];

              this.widgetListLite = [
                ...this.widgetListLite,
                {
                  id: w.id,
                  title: w.title,
                  type: w.component.name
                }
              ];
            });

          this.config = { ...config, period, widgets };
          this.filters = filters;

          return this.config;
        });
      } else {
        const body = await fetchResp.json();
        return Promise.reject({
          code: fetchResp.status,
          text: fetchResp.statusText,
          detail: body.detail
        });
      }
    }
  }

  getWidget(id: number): Widget | undefined {
    return this.config
      ? (this.config.widgets.find((w: Widget) => w.id === id) as Widget)
      : undefined;
  }

  getWidgets(ids: number[]): Widget[] {
    if (!this.config) return [];
    else {
      return ids
        .map((id: number) => this.getWidget(id))
        .filter((w) => !!w) as Widget[];
    }
  }

  getLSFavourites(workspaceId: string): number[] {
    const favourites = localStorage.getItem(
      `${LOCAL_STORAGE_KEY}${workspaceId}`
    );
    return favourites ? JSON.parse(favourites) : [];
  }

  async getFavourites(workspaceId: string): Promise<number[]> {
    const url = `${APP_NEXUS_API_BASE_URL}/analytics/${workspaceId}/me/settings`;
    const favourites = await apiGet<number[]>(
      url,
      getUserSession().access_token
    );

    const lsFavourites = this.getLSFavourites(workspaceId);

    if (lsFavourites.length > 0 && !isEqualObject(favourites, lsFavourites)) {
      this.saveFavourites(workspaceId, lsFavourites);
      return lsFavourites;
    } else {
      if (lsFavourites.length === 0) {
        localStorage.setItem(
          `${LOCAL_STORAGE_KEY}${workspaceId}`,
          JSON.stringify(favourites)
        );
      }
      return favourites;
    }
  }

  async saveFavourites(
    workspaceId: string,
    widgets: number[]
  ): Promise<{ widgets: number[] }> {
    const url = `${APP_NEXUS_API_BASE_URL}/analytics/me/settings`;

    localStorage.setItem(
      `${LOCAL_STORAGE_KEY}${workspaceId}`,
      JSON.stringify(widgets)
    );
    return apiPost(
      url,
      { workspace_id: workspaceId, widgets },
      getUserSession().access_token
    );
  }

  isWidgetStorageEvent(e: StorageEvent, workspaceId: string): boolean {
    return e.key === `${LOCAL_STORAGE_KEY}${workspaceId}`;
  }

  // TODO: improve this method to use a more generic storage service
  async getPeriods(): Promise<AnalyticsPeriodOptions> {
    // const url = `${APP_NEXUS_API_BASE_URL}/analytics/periods`;
    // return apiGet(url, getUserSession().access_token);
    // Mocked for testing purposes
    return Promise.resolve({
      quarter: [],
      half_year: [],
      year: []
    });
  }
}

const AnalyticsService = new AnalyticsServiceClass();

export default AnalyticsService;
